<template>
  <accordion-panel
    id="calculated-temperature-accordion"
    class="calculated-temperature__accordion"
    :collapsed.sync="collapsed"
  >
    <template #title>
      <strong> {{ $t('engineering.cooling-efficiency.calculated-temperature') }} </strong>
    </template>
    <div v-if="loading">
      <s-loader
        class="my-5"
        size="lg"
      />
    </div>
    <div v-else>
      <div class="calculated-temperature__legends-wrapper">
        <div
          v-for="(legend, index) in legends"
          class="calculated-temperature__legend"
          :class="legendsAmountCssClass"
          :key="index"
        >
          <i
            class="fas fa-square-full calculated-temperature__legend-box"
            aria-hidden="true"
            :class="legend.class"
          />
          <span>
            {{ $t(legend.label) }}
          </span>
        </div>
      </div>
      <div class="calculated-temperature__chart-wrapper">
        <e-chart
          class="calculated-temperature__chart w-100"
          autoresize
          ref="calculatedTemperature"
          :options="options"
          @rendered="onRendered"
        />
        <img
          class="calculated-temperature__image w-100"
          alt="chart-image"
          :src="chartImg"
        >
        <div class="calculated-temperature__last-register-wrapper mt-2">
          <div
            v-show="showExponential"
            class="calculated-temperature__last-register"
          >
            <strong>
              {{ $t('engineering.cooling-efficiency.exponential-last-register') }}:
            </strong>
            {{ exponentialLastRegister }}
          </div>
          <span v-show="showDivider">
            ||
          </span>
          <div
            v-show="showDifferential"
            class="calculated-temperature__last-register"
          >
            <strong>
              {{ $t('engineering.cooling-efficiency.differential-last-register') }}:
            </strong>
            {{ differentialLastRegister }}
          </div>
        </div>
      </div>
    </div>
  </accordion-panel>
</template>

<script>
import sortBy from 'lodash/sortBy';
import { mapGetters } from 'vuex';
import { CoolingEfficiencyVariables } from 'sigma-mappings';
import AccordionPanel from '@/components/AccordionPanel';
import themeService from '@/services/themeService';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import engineeringQuickChartsMixin
  from '@/domain/mixins/installation/engineering/engineeringQuickChartsMixin';

export default {
  name: 'CalculatedTemperatureChart',
  props: {
    chartData: {
      type: Object,
      default: () => ({}),
    },
    labelSize: {
      type: Number,
      default: 10,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AccordionPanel,
  },
  mixins: [engineeringQuickChartsMixin],
  data: () => ({
    collapsed: false,
  }),
  computed: {
    ...mapGetters(['isPrinting']),
    showExponential() {
      const { exponential } = (this.chartData.lastRegister || {});
      return exponential;
    },
    showDifferential() {
      const { differential } = (this.chartData.lastRegister || {});
      return differential;
    },
    showDivider() {
      const { exponential, differential } = (this.chartData.lastRegister || {});
      return exponential && differential;
    },
    exponentialLastRegister() {
      const exponentialNumber = (this.chartData.lastRegister || {}).exponential;
      return `${this.$n(exponentialNumber)} °C`;
    },
    differentialLastRegister() {
      const exponentialNumber = (this.chartData.lastRegister || {}).differential;
      return `${this.$n(exponentialNumber)} °C`;
    },
    lineStyleOptions() {
      return {
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_HIGH]: {
          color: themeService.getStyleVar('color-warning'),
        },
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_VERY_HIGH]: {
          color: `${themeService.getStyleVar('color-danger')}8c`,
        },
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_NEGATIVE_HIGH]: {
          color: themeService.getStyleVar('color-warning'),
        },
        [CoolingEfficiencyVariables
          .PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_NEGATIVE_VERY_HIGH]: {
          color: `${themeService.getStyleVar('color-danger')}8c`,
        },
        [CoolingEfficiencyVariables.CALCULATED_EXPONENTIAL_MEASURED_OIL_TEMPERATURE_DIFFERENCE]: {
          color: themeService.getStyleVar('chart-color-blue'),
          shadowColor: 'rgba(0, 0, 0, 0.15)',
          shadowBlur: 2,
          shadowOffsetY: 3,
        },
        [CoolingEfficiencyVariables.CALCULATED_DIFFERENTIAL_MEASURED_OIL_TEMPERATURE_DIFFERENCE]: {
          color: themeService.getPrimaryColor(),
          shadowColor: 'rgba(0, 0, 0, 0.15)',
          shadowBlur: 2,
          shadowOffsetY: 3,
        },
      };
    },
    legends() {
      const { variables } = this.chartData;
      if (!variables) return [];
      const newVariables = [];
      const returnedVariables = variables.map(it => it.name);
      const baseDictionary = 'engineering.cooling-efficiency.calculated-temperature-variables';
      const possibleLegends = {
        [CoolingEfficiencyVariables.CALCULATED_DIFFERENTIAL_MEASURED_OIL_TEMPERATURE_DIFFERENCE]: {
          label: `${baseDictionary}.difference-between-measured-and-calculated-oil-temperatures-differential`,
          class: 'calculated-temperature__legend-box--primary',
          order: 0,
        },
        [CoolingEfficiencyVariables.CALCULATED_EXPONENTIAL_MEASURED_OIL_TEMPERATURE_DIFFERENCE]: {
          label: `${baseDictionary}.difference-between-measured-and-calculated-oil-temperatures-exponential`,
          class: 'calculated-temperature__legend-box--secondary',
          order: 1,
        },
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_HIGH]: {
          label: `${baseDictionary}.parameterized-maximum-allowable-difference-high`,
          class: 'calculated-temperature__legend-box--warning',
          order: 2,
        },
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_VERY_HIGH]: {
          label: `${baseDictionary}.parameterized-maximum-allowable-difference-very-high`,
          class: 'calculated-temperature__legend-box--danger',
          order: 3,
        },
        [CoolingEfficiencyVariables.PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_NEGATIVE_HIGH]: {
          label: `${baseDictionary}.parameterized-maximum-allowable-difference-high`,
          class: 'calculated-temperature__legend-box--warning',
          order: 4,
        },
        [CoolingEfficiencyVariables
          .PARAMETERIZED_MAXIMUM_ALLOWABLE_DIFFERENCE_NEGATIVE_VERY_HIGH]: {
          label: `${baseDictionary}.parameterized-maximum-allowable-difference-very-high`,
          class: 'calculated-temperature__legend-box--danger',
          order: 5,
        },
      };
      returnedVariables.forEach((legend) => {
        if (!newVariables.some(variable => variable.label === possibleLegends[legend].label)) {
          newVariables.push(possibleLegends[legend]);
        }
      });
      return sortBy(newVariables, ['order']);
    },
    legendsAmountCssClass() {
      return {
        'calculated-temperature__legend--one-method': (this.legends || []).length === 3,
      };
    },
    options() {
      const series = this.getChartSeries();
      return {
        backgroundColor: this.bgColor,
        tooltip: {
          show: true,
          confine: false,
          trigger: 'item',
          borderColor: themeService.getStyleVar('color-tooltip-border'),
          formatter: this.getTooltipFormatter,
          axisPointer: {
            type: 'cross',
            label: {
              show: false,
            },
            crossStyle: {
              opacity: 0,
            },
          },
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: this.labelSize,
          },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
          scale: true,
          splitNumber: 4,
          minInterval: 1,
          maxInterval: 25,
          axisLabel: {
            show: true,
            onZero: false,
            showMinLabel: false,
            fontSize: this.labelSize,
            color: this.fontColor,
            formatter: value => `${this.$n(value)}${this.chartData.unit}`,
          },
          splitLine: {
            show: false,
          },
        },
        grid: {
          top: 20,
          bottom: 40,
          right: 30,
          left: 50,
        },
        series,
      };
    },
  },
  methods: {
    getTooltipFormatter({ value }) {
      return `${this.$n(value[1])} ${this.chartData.unit}`;
    },
    dateFormatter(date) {
      return dateTimeFormatter(date, formats.CHART);
    },
    getChartSeries() {
      const series = [];
      const values = this.chartData?.variables || [];
      values.forEach((value) => {
        const showSymbol = value.values?.length < 2;
        const lineOptions = this.lineStyleOptions[value.name];
        series.push({
          data: value.values.map(item => [this.dateFormatter(item.date), item.value]),
          type: 'line',
          lineStyle: {
            color: lineOptions.color,
            shadowColor: lineOptions.shadowColor,
            shadowBlur: lineOptions.shadowBlur,
            shadowOffsetY: lineOptions.shadowOffsetY,
          },
          itemStyle: {
            color: lineOptions.color,
            borderWidth: 1,
          },
          showSymbol,
        });
      });
      return series;
    },
    async onRendered() {
      await this.$nextTick();
      this.chartImg = this.collapsed || !this.$refs.calculatedTemperature.height
        ? '#'
        : this.$refs.calculatedTemperature.getDataURL();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables.sass"
@import "src/styles/defaults/mobile-first"

.calculated-temperature
  &__legends-wrapper
    width: 100%
    display: flex
    flex-direction: row
    flex-wrap: nowrap

  &__chart
    max-height: 300px
    min-width: 400px
    border: 1px solid var(--color-border-dark)
    border-radius: 5px

  &__chart-wrapper
    max-width: 100%
    overflow-x: scroll
    padding: 40px 10px 10px 20px
    border: 1px solid var(--color-border-dark)
    box-sizing: border-box
    border-radius: 5px

  &__legend
    position: relative
    display: flex
    flex-wrap: nowrap
    align-items: center
    max-width: 25%
    padding: 10px

    &--one-method
      max-width: 33%

  &__accordion
    box-shadow: none

  &__legend-box
    margin: 0 10px

    &--danger
      color: var(--color-danger)
      opacity: .5

    &--warning
      color: var(--color-warning)

    &--primary
      color: var(--color-primary)

    &--secondary
      color: var(--chart-color-blue)

  &__last-register-wrapper
    width: 100%
    display: flex
    flex-wrap: nowrap
    justify-content: flex-end
    align-items: center
    +media-max-sm()
      justify-content: flex-start

  &__last-register
    font-size: .8em
    +media-min-sm
      margin: 0 10px

  &__divider
    font-size: .8em
    padding-left: 10px
    padding-right: 10px

  &__image
    display: none

+media-min-md()
  .calculated-temperature
    &__chart-wrapper
      overflow-x: hidden

@media screen
  +media-max-sm
    .calculated-temperature
      &__legends-wrapper
        display: block
        flex-direction: unset
        flex-wrap: unset

      &__legend
        display: block
        max-width: 100%

::v-deep
  .accordion-panel
    &__header
      padding: 2px 15px !important
      font-weight: bold

@media print
  .calculated-temperature
    &__chart
      display: none

    &__image
      display: block

</style>
